// NotificationsPreview — the focus-mode preview for the `notifications` slot.
// A toast only appears at runtime when its trigger fires, so a static render
// shows nothing. Instead we list each configured notification with a "Show"
// button; clicking it pops that notification's message as a real .sd-toast so
// the operator can see the wording + styling. This is preview-only chrome — it
// doesn't simulate the actual app trigger, just the visual result.

import { useState } from "react";
import type { BlockInstance } from "../blocks/types";

/** Friendly label for each known trigger, so the list reads in plain language
 *  rather than the raw enum value. Unknown triggers fall back to the raw id. */
const TRIGGER_LABELS: Record<string, string> = {
  "product-tap-no-drag": "Product tapped without dragging (touch)",
  "product-click-no-drag": "Product clicked without dragging (mouse)",
  "drag-cancelled": "Drag cancelled (dropped off-scene)",
  "scene-empty-on-cart": "Cart opened while empty",
};

export function NotificationsPreview({ entries }: { entries: BlockInstance[] }) {
  // The notification currently shown as a toast (by block key), or null.
  const [shownKey, setShownKey] = useState<string | null>(null);

  const notifications = entries.filter(b => b.block === "notification");
  const shown = notifications.find(b => b.key === shownKey);
  const shownMessage =
    shown && typeof shown.props?.text === "string" ? shown.props.text : "";

  return (
    <div className="sd-notif-preview">
      <p className="sd-notif-preview__hint">
        Notifications only appear when their trigger fires in the configurator.
        Click <strong>Show</strong> to preview how each one looks.
      </p>

      {notifications.length === 0 ? (
        <p className="sd-notif-preview__empty">
          No notifications yet — add a Notification block, pick a trigger, and
          write the message.
        </p>
      ) : (
        <ul className="sd-notif-preview__list">
          {notifications.map(b => {
            const trigger = typeof b.props?.trigger === "string" ? b.props.trigger : "";
            const message = typeof b.props?.text === "string" ? b.props.text : "";
            return (
              <li key={b.key} className="sd-notif-preview__row">
                <div className="sd-notif-preview__meta">
                  <span className="sd-notif-preview__trigger">
                    {TRIGGER_LABELS[trigger] ?? trigger ?? "(no trigger)"}
                  </span>
                  <span className="sd-notif-preview__message">
                    {message || <em>(no message)</em>}
                  </span>
                </div>
                <button
                  type="button"
                  className="sd-notif-preview__show"
                  onClick={() => setShownKey(b.key)}
                  disabled={!message}
                >
                  Show
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* The live toast — same markup/classes as the real NotificationsHost so
          it previews the actual styling. */}
      {shown && shownMessage && (
        <div className="sd-toasts" role="region" aria-label="Notification preview">
          <div className="sd-toast" role="status" aria-live="polite">
            <span className="sd-toast__message">{shownMessage}</span>
            <button
              type="button"
              className="sd-toast__close"
              aria-label="Dismiss"
              onClick={() => setShownKey(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
